import Head from 'next/head';
import { useRouter } from 'next/router';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import Balancer from 'react-wrap-balancer';

import { homecleanState } from 'atoms';
import { useRecoilState } from 'recoil';
import styled, { css } from 'styled-components';

import { AndroidDownloadIcon, IosDownloadIcon } from '@components/common/Icons';
import { Container } from '@components/parts/Container';
import { ActionableLink } from '@components/parts/Link';

import { sendFbqEvent } from '@utils/analytics';
import { MISO_APP_STORES, PAGE_PATH } from '@utils/constants';

import { UploadedImage } from '@customTypes/UploadedImage';

import BannerIframeContents from './BannerIframeContents';

type WrapperProps = {
  isWithoutCRA: boolean;
  isInternet: boolean;
  isHomeclean: boolean;
  isNewBanner: boolean;
};

type Action = {
  href: string;
  name: string;
};
interface BannerProps {
  image: UploadedImage;
  hero: string;
  description: string;
  action: Action | null;
  mobileImage?: UploadedImage;
  isNewBanner?: boolean;
  scrollToId?: string;
}

const Wrapper = styled.div<WrapperProps>`
  position: relative;
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  min-height: 480px;
  height: ${({ isWithoutCRA }) => (isWithoutCRA ? '1400px' : 'calc(var(--initial-app-height) + 10px)')};
  @media (max-width: 739px) {
    ${({ isInternet }) =>
      isInternet &&
      css`
        height: 1770px;
      `};
  }
  @media (max-width: 430px) {
    height: ${({ isHomeclean, isWithoutCRA }) =>
      isHomeclean ? '1017px' : isWithoutCRA ? '1600px' : 'calc(var(--initial-app-height) - 60px)'};
  }
`;

const BannerContentWrapper = styled(Container)<{ isNewBanner: boolean }>`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;

  .container {
    @media (max-width: 993px) {
      width: calc(100% - 40px);
      height: ${(props) => (props.isNewBanner ? '100%' : 'auto')};
      margin-right: ${(props) => (props.isNewBanner ? '0' : 'auto')};
      margin-left: ${(props) => (props.isNewBanner ? '0' : 'auto')};
      padding: 0;
    }
  }
`;

const BannerContent = styled.div<{ isNewBanner: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;

  @media (min-width: 912px) {
    position: unset;
  }

  @media (max-width: 768px) {
    justify-content: space-between;
    padding: ${(props) => (props.isNewBanner ? '80px 0 30px 0' : '180px 0 70px 0')};
  }
`;

const BannerHeroWrapper = styled.div`
  display: flex;
  gap: 60px;
  transform: translate(0px, 16px);
  opacity: 0;
  filter: blur(4px);
  animation: fade-in 0.5s 1s forwards cubic-bezier(0.11, 0, 0.5, 0);
  @keyframes fade-in {
    100% {
      opacity: 1;
      filter: blur(0);
      transform: translate(0);
    }
  }
  @media (max-width: 430px) {
    flex-direction: column;
    margin-bottom: 30px;
    gap: 0px;
  }
`;

const BannerHero = styled.h1`
  color: white;
  width: 100%;
  min-width: 300px;
  font-weight: 700;
  white-space: pre-wrap;
`;

const BannerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  z-index: 0;
  position: absolute;
`;

const BannerDescription = styled.div`
  margin-top: 15px;
  color: white;
  font-size: 25px;
  font-weight: lighter;
`;

const BannerAction = styled(ActionableLink)`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-top: 60px;
  padding: 15px 20px;
  width: 100%;
  background-color: #055cf5;
  @media (min-width: 933px) {
    max-width: 300px;
  }
  border-radius: 4px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  opacity: 0;
  transform: translateY(16px);
  will-change: transform;
  animation: action-in 0.3s 1.7s forwards cubic-bezier(0.11, 0, 0.5, 0);
  @keyframes action-in {
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MobileBannerAction = styled(BannerAction)`
  display: none;
  @media (max-width: 932px) {
    display: flex;
  }
`;

const DownloadButtonsContainer = styled.div`
  display: none;
  gap: 20px;
  margin-top: 60px;
  opacity: 0;
  transform: translateY(16px);
  will-change: transform;
  animation: action-in 0.3s 1.7s forwards cubic-bezier(0.11, 0, 0.5, 0);

  @media (min-width: 933px) {
    display: flex;
  }

  @keyframes action-in {
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DownloadButton = styled(ActionableLink)`
  cursor: pointer;
`;

const ScrollDown = styled.div`
  position: absolute;
  display: none;
  align-items: center;
  justify-content: center;
  @media (min-width: 912px) {
    display: flex;
    bottom: 60px;
  }
  left: 0;
  right: 0;

  animation: arrow_motion 1s linear 0s infinite alternate;
  @keyframes arrow_motion {
    0% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(16px);
    }
  }
`;

const Banner: React.FC<BannerProps> = ({
  image,
  hero,
  description,
  action,
  mobileImage,
  isNewBanner = false,
  scrollToId,
}) => {
  const [isInView, setIsInView] = useState<boolean | null>(null);
  const [homecleanIframe] = useRecoilState(homecleanState);
  // const resetHomecleanIframe = useResetRecoilState(homecleanState);
  const [isInterentIframe, setIsInterentIframe] = useState(false);
  // NOTE: internet deal을 api => zapier 로 통신하기 위한 code 241028일 제거 예정
  const [isInterentZapierIframe, setIsInterentZapierIframe] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState({
    desktop: '',
    tablet: '',
    mobile: '',
  });
  const router = useRouter();

  const handleChange = (isVisible: boolean) => {
    if (isInView !== isVisible) {
      const doc = document.documentElement;
      doc.style.setProperty('--display-cta', isVisible ? 'none' : 'flex');
      setIsInView(isVisible);
    }
  };

  const eventName = 'start_flow_';

  const handleActionClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (scrollToId != null) {
      event.preventDefault();
      document.getElementById(scrollToId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      sendFbqEvent({ event, eventName });
      if (event.currentTarget.href) {
        event.preventDefault();
        window.open(event.currentTarget.href, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const isWithoutCRA = useMemo(() => {
    return homecleanIframe.isHomeclean || isInterentIframe || isInterentZapierIframe;
  }, [homecleanIframe, isInterentIframe, isInterentZapierIframe]);

  const checkIframeContents = useCallback(() => {
    setIsInterentIframe(false);

    const pathname = router.query.slug as string;
    switch (pathname) {
      case PAGE_PATH.INTERNET:
        setIsInterentIframe(true);
        return;
      case PAGE_PATH.INTERNET_ZAPIER:
        setIsInterentZapierIframe(true);
        return;
      // HC 예약 페이지 삭제 처리, 다시 복구할 때 주석만 해제하면 가능
      // case PAGE_PATH.HOMECLEAN:
      // setHomecleanIframe((prev) => ({ ...prev, isHomeclean: true }));
      // return;
      default:
        setIsInterentIframe(false);
        // resetHomecleanIframe();
        return;
    }
  }, [router]);

  const getBannerImageUrl = useCallback(() => {
    const desktop = image.data.attributes?.url || '';
    const tablet = image.data.attributes?.formats?.medium?.url || '';
    const mobile = mobileImage?.data?.attributes?.url || image.data.attributes?.formats?.medium?.url || '';

    setBackgroundImage({ desktop, tablet, mobile });
  }, [image, mobileImage]);

  useEffect(() => {
    checkIframeContents();
  }, [checkIframeContents]);

  useEffect(() => {
    setBackgroundImage({ desktop: '', tablet: '', mobile: '' });
    getBannerImageUrl();
  }, [getBannerImageUrl]);

  return (
    <>
      <VisibilitySensor partialVisibility onChange={handleChange}>
        <Wrapper
          id="scrollTarget"
          isWithoutCRA={isWithoutCRA}
          isInternet={isInterentIframe}
          isHomeclean={homecleanIframe.isHomeclean}
          isNewBanner={isNewBanner}
        >
          {backgroundImage.desktop ? (
            <BannerImage
              src={backgroundImage.desktop}
              width={500}
              height={500}
              alt=""
              srcSet={`${backgroundImage.desktop} 1024w, ${backgroundImage.tablet} 769w, ${backgroundImage.mobile} 430w`}
              sizes="(min-width: 1024px) 1024px, 
          (min-width: 769px) 769px, 
          430px"
            />
          ) : null}
          <Head>
            <link rel="preload" href={image?.data?.attributes?.formats?.small?.url || ''} as="image" />
          </Head>
          <BannerContentWrapper isNewBanner={isNewBanner}>
            <BannerContent isNewBanner={isNewBanner}>
              <BannerHeroWrapper>
                <Balancer>
                  <BannerHero>{hero}</BannerHero>
                </Balancer>
              </BannerHeroWrapper>
              <BannerDescription>{description}</BannerDescription>
              {action && !isWithoutCRA && (
                <>
                  <DownloadButtonsContainer>
                    <DownloadButton
                      href={MISO_APP_STORES.IOS}
                      onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => handleActionClick(event)}
                    >
                      <IosDownloadIcon />
                    </DownloadButton>
                    <DownloadButton
                      href={MISO_APP_STORES.ANDROID}
                      onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => handleActionClick(event)}
                    >
                      <AndroidDownloadIcon />
                    </DownloadButton>
                  </DownloadButtonsContainer>
                  <MobileBannerAction
                    href={action.href}
                    onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => handleActionClick(event)}
                  >
                    {action.name}
                  </MobileBannerAction>
                </>
              )}
              <ScrollDown>
                <svg width="22" height="12" viewBox="0 0 22 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0.916343 1.4165L10.9072 10.5832L21.083 1.4165" stroke="white" strokeWidth="3" />
                </svg>
              </ScrollDown>
            </BannerContent>
          </BannerContentWrapper>
        </Wrapper>
      </VisibilitySensor>
      {/* iframe 들어가는 service들 */}
      {isWithoutCRA ? (
        <BannerIframeContents isInternet={isInterentIframe} isInterentZapier={isInterentZapierIframe} />
      ) : null}
    </>
  );
};

export default Banner;
